import { inject, Injectable } from '@angular/core'
import { map, Observable } from 'rxjs'
import { TimeInterval } from '../common/time-interval/time-interval.component'
import {
  CallScore,
  MeetingRoomMetric,
  normalizeOrganizationUsageMetric,
  normalizeUsageByUserMetric,
  RawUsageByOrganizationMetric,
  RawUsageByUserMetric,
  UsageByOrganizationMetric,
  UsageByUserMetric,
} from '../metrics.service'
import { EnvironmentFanOutService, EnvironmentResult, FanOutParams } from '../multi-environment/environment-fan-out.service'
import { EnvironmentTagged, FanOut, tagRows, toFanOut, unscopeIds } from '../multi-environment/environment-merge'
import { SupportEnvironment } from '../multi-environment/support-environment'

/**
 * Which family of metrics routes a screen reads. The support routes answer for any organization and
 * are the only ones Atlas may reach cross-origin; the admin ones are scoped to the caller's own
 * client, which is all a customer's own dashboard is entitled to.
 */
export type AnalyticsScope = 'admin' | 'support'

function pathFor(scope: AnalyticsScope, name: string): string {
  return scope === 'support' ? `/api/metrics/support/${name}` : `/api/metrics/${name}`
}

type UserOfMetric = UsageByUserMetric['user']

/**
 * A usage row with its environment on it, and on its organization too: the organization is what the
 * table renders a link out of, and it reaches that cell on its own, so it has to carry which
 * environment's organization it is.
 */
export type UsageByUserRow = EnvironmentTagged<Omit<UsageByUserMetric, 'user'>> & {
  user: Omit<UserOfMetric, 'organization'> & { organization: EnvironmentTagged<UserOfMetric['organization']> | null }
}

export type UsageByOrganizationRow = EnvironmentTagged<UsageByOrganizationMetric>
export type CallScoreRow = EnvironmentTagged<CallScore>
export type MeetingRoomRow = EnvironmentTagged<MeetingRoomMetric>

/**
 * An environment that owns none of the selected organizations is not asked at all: narrowing to one
 * organization would otherwise cost a request to every other environment for an answer that is
 * always empty. An empty selection reaches all of them, which is how the unfiltered screen loads.
 */
export function selectionReaches(organizationIds: string[]): (environment: SupportEnvironment) => boolean {
  return (environment) => organizationIds.length === 0 || unscopeIds(organizationIds, environment).length > 0
}

// Each environment is sent its own organizations and no others: the same id names a different one in
// each of them.
export function organizationParams(environment: SupportEnvironment, organizationIds: string[]): FanOutParams {
  const forEnvironment = unscopeIds(organizationIds, environment)
  return forEnvironment.length > 0 ? { organizationIds: forEnvironment } : {}
}

function fromDateParams(fromDate: Date | null): FanOutParams {
  return fromDate === null ? {} : { fromDateString: fromDate.toISOString() }
}

function intervalParams(interval: TimeInterval): FanOutParams {
  return {
    ...(interval.from !== null ? { fromDateString: interval.from.toISOString() } : {}),
    ...(interval.to !== null ? { toDateString: interval.to.toISOString() } : {}),
  }
}

// Sorted so that picking the same organizations in a different order is the same query rather than a
// second one; the environment's own slice of it is derived from this, not keyed separately.
function selectionKey(organizationIds: string[]): string[] {
  return [...organizationIds].sort()
}

function normalized<TRaw, TRow>(results: EnvironmentResult<TRaw>[], normalize: (raw: TRaw) => TRow[]): EnvironmentResult<TRow[]>[] {
  return results.map((result) => ({ ...result, data: result.data === null ? null : normalize(result.data) }))
}

export type UsageInterval = UsageByUserMetric['usageByInterval'][number]

/**
 * Every environment builds its own interval axis from the same start date, so the axes normally
 * agree — but they are cut against each environment's own clock, and two answers either side of a
 * midnight do not have the same buckets. The table reads its columns off the first row and the chart
 * reads its labels off the first series, so a row from the shorter axis would sit one bucket out, or
 * be indexed past its own end.
 *
 * Putting every row on the union of the axes makes a column and a bar mean the interval they are
 * named after on every row, rather than whichever one it occupied in its own environment's answer.
 * The labels are 'YYYY-MM' or 'YYYY-MM-DD', so sorting them is chronological.
 */
export function alignIntervals<T extends { usageByInterval: UsageInterval[] }>(rows: T[]): T[] {
  const intervals = rows.flatMap((row) => row.usageByInterval)
  const axis = [...new Set(intervals.map((interval) => interval.intervalLabel))].sort()
  const intervalType = intervals[0]?.intervalType ?? 'MONTH'
  return rows.map((row) => ({
    ...row,
    // A bucket this environment never reported is not zero usage; it is an interval it was not asked
    // about, which the table and the chart both render as absent rather than as a value.
    usageByInterval: axis.map(
      (intervalLabel) =>
        row.usageByInterval.find((interval) => interval.intervalLabel === intervalLabel) ?? { intervalLabel, intervalType, duration: null, count: null }
    ),
  }))
}

/**
 * What a panel shows while the environments are answering. Every panel these feed is a total or a
 * ranking over the whole merged set, so it holds its loading state until nothing is still being
 * checked.
 *
 * Showing the environments that have answered so far reads as a finished number that happens to be
 * wrong, and the environments differ by more than an order of magnitude in how long they take. The
 * reader sees a settled figure, ticks one more organization into the filter, and watches the total
 * fall — the slow environment's rows having been dropped for the new selection and not yet replaced.
 *
 * An environment that cannot be read at all is not "still checking", so a signed-out or unreachable
 * one does not hold the screen: its rows stay missing and the connection banner is what says so.
 */
export function loadState(result: FanOut<unknown[]>): { isLoading: boolean; isError: boolean } {
  return { isLoading: result.summary.isChecking, isError: result.summary.hasNothingToShow }
}

export function tagUsageByUser(results: EnvironmentResult<UsageByUserMetric[]>[]): UsageByUserRow[] {
  return results.flatMap((result) =>
    (result.data ?? []).map((row) => ({
      ...row,
      environment: result.environment,
      user: {
        ...row.user,
        organization: row.user.organization === null ? null : { ...row.user.organization, environment: result.environment },
      },
    }))
  )
}

/**
 * The analytics screens' reads, fanned out across the configured environments and merged back into
 * one set of rows. Every row keeps the environment it came from: ids are per-database, so the same
 * number is a different organization, user and meeting in each of them.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsDashboardService {
  private fanOutService = inject(EnvironmentFanOutService)

  readonly environments = this.fanOutService.environments
  readonly isMultiEnvironment = this.environments.length > 1

  usageByOrganization(scope: AnalyticsScope, organizationIds: string[], fromDate: Date | null): Observable<FanOut<UsageByOrganizationRow[]>> {
    return this.fanOutService
      .fanOut<RawUsageByOrganizationMetric>({
        path: pathFor(scope, 'usage-by-organization'),
        params: (environment) => ({ ...organizationParams(environment, organizationIds), ...fromDateParams(fromDate) }),
        queryKey: ['metrics', scope, 'usage-by-organization', selectionKey(organizationIds), fromDate?.toISOString() ?? null],
        appliesTo: selectionReaches(organizationIds),
      })
      .pipe(map((results) => toFanOut(results, (all) => alignIntervals(tagRows(normalized(all, normalizeOrganizationUsageMetric))))))
  }

  usageByUser(scope: AnalyticsScope, organizationIds: string[], fromDate: Date | null): Observable<FanOut<UsageByUserRow[]>> {
    return this.fanOutService
      .fanOut<RawUsageByUserMetric>({
        path: pathFor(scope, 'usage-by-user'),
        params: (environment) => ({ ...organizationParams(environment, organizationIds), ...fromDateParams(fromDate) }),
        queryKey: ['metrics', scope, 'usage-by-user', selectionKey(organizationIds), fromDate?.toISOString() ?? null],
        appliesTo: selectionReaches(organizationIds),
      })
      .pipe(map((results) => toFanOut(results, (all) => alignIntervals(tagUsageByUser(normalized(all, normalizeUsageByUserMetric))))))
  }

  callScores(scope: AnalyticsScope, organizationIds: string[], interval: TimeInterval): Observable<FanOut<CallScoreRow[]>> {
    const params = intervalParams(interval)
    return this.fanOutService
      .fanOut<CallScore[]>({
        path: pathFor(scope, 'call-scores'),
        params: (environment) => ({ ...organizationParams(environment, organizationIds), ...params }),
        queryKey: ['metrics', scope, 'call-scores', selectionKey(organizationIds), params],
        appliesTo: selectionReaches(organizationIds),
      })
      .pipe(map((results) => toFanOut(results, tagRows)))
  }

  meetingRooms(scope: AnalyticsScope, organizationIds: string[], interval: TimeInterval): Observable<FanOut<MeetingRoomRow[]>> {
    const params = intervalParams(interval)
    return this.fanOutService
      .fanOut<MeetingRoomMetric[]>({
        path: pathFor(scope, 'meeting-rooms'),
        params: (environment) => ({ ...organizationParams(environment, organizationIds), ...params }),
        queryKey: ['metrics', scope, 'meeting-rooms', selectionKey(organizationIds), params],
        appliesTo: selectionReaches(organizationIds),
      })
      .pipe(map((results) => toFanOut(results, tagRows)))
  }
}
